/**
 * Verify Email Page
 *
 * Handles email verification after signup.
 * Shows loading state while verifying, then success or error.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { verifyEmail, resendVerification, getCurrentUser } from '../../api/auth';
import { getErrorMessage } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

type VerificationState = 'loading' | 'success' | 'error' | 'no-token';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  const [state, setState] = useState<VerificationState>(token ? 'loading' : 'no-token');
  const [error, setError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Verification tokens are single-use, so the POST must fire exactly once.
  // Without this guard the effect re-ran when its function deps changed
  // identity after the first success (refreshUser logs the user in →
  // context re-renders), and the SECOND call — with the now-consumed token —
  // overwrote the success screen with "Invalid or Expired Token" while the
  // user was in fact verified and logged in. Found in production 2026-08-08.
  const attemptedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('no-token');
      return;
    }
    if (attemptedToken.current === token) return;
    attemptedToken.current = token;

    const verify = async () => {
      try {
        await verifyEmail({ token });
        setState('success');
        // Refresh user context
        await refreshUser();
        // Redirect to admin after a short delay
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } catch (err) {
        // Belt and braces: a "failed" verify may mean an earlier attempt
        // already succeeded and consumed the token (double-open, refreshed
        // tab, mail-client prefetch). If a session exists, the user IS
        // verified — treat it as success instead of dead-ending them.
        try {
          await getCurrentUser();
          setState('success');
          await refreshUser();
          setTimeout(() => {
            navigate('/admin');
          }, 2000);
          return;
        } catch {
          // genuinely not verified / not logged in — fall through
        }
        setState('error');
        setError(getErrorMessage(err));
      }
    };

    verify();
  }, [token, navigate, refreshUser]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;

    setIsResending(true);
    setResendMessage(null);

    try {
      const response = await resendVerification({ email: resendEmail });
      setResendMessage(response.message);
    } catch (err) {
      setResendMessage(getErrorMessage(err));
    } finally {
      setIsResending(false);
    }
  };

  // No token provided
  if (state === 'no-token') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
          <div className="w-16 h-16 bg-yellow-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Invalid Verification Link
          </h2>
          <p className="text-gray-600 mb-6">
            This verification link is missing the required token. Please use the link from your email.
          </p>
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-500 font-medium"
          >
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
          <div className="w-16 h-16 bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="animate-spin w-8 h-8 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Verifying Your Email
          </h2>
          <p className="text-gray-600">
            Please wait while we verify your email address...
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-4">
        <div className="max-w-md w-full bg-white p-8 text-center border border-hairline">
          <div className="w-16 h-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Email Verified!
          </h2>
          <p className="text-gray-600 mb-4">
            Your email has been verified successfully. Redirecting you to the dashboard...
          </p>
          <Link
            to="/admin"
            className="text-primary-600 hover:text-primary-500 font-medium"
          >
            Go to dashboard now
          </Link>
        </div>
      </div>
    );
  }

  // Error state with resend option
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="max-w-md w-full bg-white p-8 border border-hairline">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Verification Failed
          </h2>
          <p className="text-gray-600">
            {error || 'This verification link is invalid or has expired.'}
          </p>
        </div>

        {/* Resend Form */}
        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-600 mb-4 text-center">
            Need a new verification link? Enter your email below.
          </p>

          <form onSubmit={handleResend} className="space-y-4">
            {resendMessage && (
              <div className="bg-surface-panel border border-hairline border-l-[3px] border-l-rack text-ink-body px-4 py-3 text-sm">
                {resendMessage}
              </div>
            )}

            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Enter your email"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isResending || !resendEmail}
              className={`
                w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md
                shadow-sm text-sm font-medium text-white bg-primary-600
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2
                focus:ring-rack transition-colors
                ${isResending || !resendEmail ? 'opacity-75 cursor-not-allowed' : ''}
              `}
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-500 font-medium"
          >
            Return to login
          </Link>
        </p>
      </div>
    </div>
  );
}
